import { useState } from 'react'
import { Image, X, RefreshCw, Plus, Maximize2 } from 'lucide-react'
import { getAssetThumbnailUrl } from '@/lib/assets'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import type { Asset } from '@/types'

interface ReferenceImageSelectorProps {
  selectedAsset?: Asset | null
  selectedAssets?: Asset[]
  onSelect: () => void
  onClear?: () => void
  onClearAsset?: (asset: Asset) => void
  canClearAsset?: (asset: Asset) => boolean
  source?: 'manual' | 'product'
  disabled?: boolean
  label?: string
  emptyTitle?: string
  emptyDescription?: string
  helperText?: string
  selectedHelperText?: string
  addButtonLabel?: string
  canAddMore?: boolean
  showLabel?: boolean
  showSourceLabel?: boolean
  multiSelectLayout?: 'stacked' | 'horizontal'
}

export function ReferenceImageSelector({
  selectedAsset = null,
  selectedAssets,
  onSelect,
  onClear,
  onClearAsset,
  canClearAsset,
  source = 'manual',
  disabled = false,
  label = 'Reference Image (Optional)',
  emptyTitle = 'Add reference image',
  emptyDescription = 'Choose from library or upload a new one',
  helperText = 'The reference image will be used as style/composition inspiration.',
  selectedHelperText,
  addButtonLabel = 'Add reference',
  canAddMore = true,
  showLabel = true,
  showSourceLabel = true,
  multiSelectLayout = 'stacked',
}: ReferenceImageSelectorProps) {
  const [previewAsset, setPreviewAsset] = useState<Asset | null>(null)
  const assets = selectedAssets ?? (selectedAsset ? [selectedAsset] : [])
  const isMultiSelect = selectedAssets !== undefined
  const useHorizontalMultiSelect = isMultiSelect && multiSelectLayout === 'horizontal'
  const horizontalMultiSelectTileCount = assets.length
  const horizontalMultiSelectClass = (() => {
    if (!useHorizontalMultiSelect) {
      return 'space-y-2'
    }
    if (horizontalMultiSelectTileCount <= 3) {
      return 'grid grid-cols-2 gap-3 sm:grid-cols-3'
    }
    if (horizontalMultiSelectTileCount >= 5) {
      return 'grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5'
    }
    if (horizontalMultiSelectTileCount === 4) {
      return 'grid grid-cols-2 gap-3 lg:grid-cols-4'
    }
    return 'grid grid-cols-2 gap-3 sm:grid-cols-3'
  })()

  const getSourceLabel = (asset: Asset) => {
    const isProductAsset = asset.id.startsWith('product-screenshot-') || asset.source_type === 'screenshot'
    if (isProductAsset) {
      return 'From product screenshot'
    }
    if (asset.source_type === 'uploaded') {
      return 'Uploaded to library'
    }
    return 'Selected from library'
  }

  const previewDialog = previewAsset ? (
    <Dialog open onOpenChange={(open) => { if (!open) setPreviewAsset(null) }}>
      <DialogContent className="max-w-5xl border-gray-200 bg-white p-3 sm:p-4">
        <div className="space-y-3">
          <DialogTitle className="pr-8 text-base font-medium text-gray-900">
            {previewAsset.file_name || 'Reference image'}
          </DialogTitle>
          <div className="overflow-hidden rounded-2xl border border-gray-200 bg-gray-50">
            {previewAsset.url ? (
              <img
                src={previewAsset.url}
                alt={previewAsset.file_name || 'Reference image'}
                className="max-h-[78vh] w-full object-contain"
              />
            ) : (
              <div className="flex h-72 items-center justify-center text-sm text-gray-500">
                Preview unavailable
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  ) : null

  if (assets.length > 0) {
    return (
      <>
        <div className="space-y-2">
          {showLabel && label && (
            <label className="block text-sm font-medium text-gray-700">
              {label}
            </label>
          )}
          <div className={horizontalMultiSelectClass}>
            {assets.map((asset, index) => {
              const isClearable = canClearAsset ? canClearAsset(asset) : Boolean(onClearAsset || onClear)
              const thumbnailUrl = getAssetThumbnailUrl(asset)
              if (useHorizontalMultiSelect) {
                return (
                  <div key={asset.id} className="min-w-0">
                    <div className="group relative">
                      <button
                        type="button"
                        onClick={() => asset.url && setPreviewAsset(asset)}
                        disabled={!asset.url}
                        className="relative block aspect-square w-full overflow-hidden rounded-2xl border border-gray-200 bg-gray-100 shadow-sm transition hover:border-gray-300 disabled:cursor-default"
                        aria-label={`Preview ${asset.file_name || 'reference image'}`}
                      >
                        {thumbnailUrl ? (
                          <img
                            src={thumbnailUrl}
                            alt={asset.file_name || 'Reference'}
                            className="h-full w-full object-contain bg-gray-50"
                            loading="lazy"
                            decoding="async"
                          />
                        ) : (
                          <div className="flex h-full items-center justify-center">
                            <Image className="h-5 w-5 text-gray-400" />
                          </div>
                        )}
                        <div className="absolute left-2 top-2 rounded-full bg-black/70 px-1.5 py-0.5 text-[10px] font-medium text-white">
                          {index + 1}
                        </div>
                        {asset.url && (
                          <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/0 opacity-0 transition group-hover:bg-black/20 group-hover:opacity-100">
                            <div className="inline-flex items-center gap-1 rounded-full bg-black/70 px-2.5 py-1 text-[10px] font-medium text-white shadow-sm">
                              <Maximize2 className="h-3 w-3" />
                              View
                            </div>
                          </div>
                        )}
                      </button>
                      {isClearable && onClearAsset && (
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation()
                            onClearAsset(asset)
                          }}
                          disabled={disabled}
                          className="absolute right-1.5 top-1.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-white text-gray-700 ring-1 ring-black/10 shadow-md transition hover:bg-red-50 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-50"
                          aria-label={`Remove ${asset.file_name || 'reference image'}`}
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      )}
                      <p className="mt-2 truncate text-xs font-medium text-gray-700">
                        {asset.file_name || 'Reference'}
                      </p>
                      {showSourceLabel && (
                        <p className="truncate text-[11px] text-gray-500">
                          {getSourceLabel(asset)}
                        </p>
                      )}
                    </div>
                  </div>
                )
              }

              return (
                <div key={asset.id} className="border border-gray-200 rounded-lg p-3">
                  <div className="flex items-start gap-3">
                    <button
                      type="button"
                      onClick={() => asset.url && setPreviewAsset(asset)}
                      disabled={!asset.url}
                      className="group relative h-20 w-20 flex-shrink-0 overflow-hidden rounded-lg bg-gray-100"
                      aria-label={`Preview ${asset.file_name || 'reference image'}`}
                    >
                      {thumbnailUrl ? (
                        <>
                          <img
                            src={thumbnailUrl}
                            alt="Reference"
                            className="h-full w-full object-contain bg-gray-50"
                            loading="lazy"
                            decoding="async"
                          />
                          <div className="absolute inset-0 flex items-center justify-center bg-black/0 transition group-hover:bg-black/20">
                            <Maximize2 className="h-4 w-4 text-white opacity-0 transition group-hover:opacity-100" />
                          </div>
                        </>
                      ) : null}
                    </button>

                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {asset.file_name || 'Reference Image'}
                      </p>
                      {showSourceLabel && (
                        <p className="text-xs text-gray-500 mt-0.5">
                          {isMultiSelect
                            ? getSourceLabel(asset)
                            : source === 'product'
                              ? 'From product screenshot'
                              : asset.source_type === 'uploaded'
                                ? 'Uploaded to library'
                                : 'Selected from library'}
                        </p>
                      )}

                      {!isMultiSelect && (
                        <div className="flex flex-wrap gap-1.5 mt-2">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={onSelect}
                            disabled={disabled}
                            className="h-7 px-2 text-xs"
                          >
                            <RefreshCw className="h-3 w-3 mr-1" />
                            Change
                          </Button>
                          {onClear && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={onClear}
                              disabled={disabled}
                              className="h-7 px-2 text-xs text-gray-500 hover:text-red-600"
                            >
                              <X className="h-3 w-3 mr-1" />
                              Remove
                            </Button>
                          )}
                        </div>
                      )}

                      {isMultiSelect && isClearable && onClearAsset && (
                        <div className="mt-2">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => onClearAsset(asset)}
                            disabled={disabled}
                            className="h-7 px-2 text-xs text-gray-500 hover:text-red-600"
                          >
                            <X className="h-3 w-3 mr-1" />
                            Remove
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )
            })}

            {isMultiSelect && canAddMore && (
              useHorizontalMultiSelect ? (
                <button
                  type="button"
                  onClick={onSelect}
                  disabled={disabled}
                  className="flex aspect-square w-full min-w-0 flex-col items-center justify-center rounded-2xl border border-dashed border-gray-300 bg-white text-gray-500 transition hover:border-gray-400 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Plus className="h-4 w-4" />
                  <span className="mt-2 px-2 text-center text-[11px] font-medium leading-4">
                    {addButtonLabel}
                  </span>
                </button>
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={onSelect}
                  disabled={disabled}
                  className="h-8 px-3 text-xs"
                >
                  <RefreshCw className="h-3 w-3 mr-1" />
                  {addButtonLabel}
                </Button>
              )
            )}
          </div>
          <p className="text-xs text-gray-500">
            {selectedHelperText || helperText}
          </p>
        </div>
        {previewDialog}
      </>
    )
  }

  return (
    <>
      <div className="space-y-2">
        {showLabel && label && (
          <label className="block text-sm font-medium text-gray-700">
            {label}
          </label>
        )}
        <button
          type="button"
          onClick={onSelect}
          disabled={disabled}
          className="w-full border-2 border-dashed border-gray-300 rounded-lg p-6 hover:border-gray-400 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <div className="flex flex-col items-center">
            <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center mb-2">
              <Image className="h-6 w-6 text-gray-400" />
            </div>
            <p className="text-sm font-medium text-gray-700">{emptyTitle}</p>
            <p className="text-xs text-gray-500 mt-1">
              {emptyDescription}
            </p>
          </div>
        </button>
        <p className="text-xs text-gray-500">
          {helperText}
        </p>
      </div>
      {previewDialog}
    </>
  )
}
